<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    :disabled="disabled"
    :z-index="zIndex"
    location="bottom start"
    transition="scale-transition"
    content-class="ncrm-date-time-field__menu"
  >
    <template #activator="{ props: menuProps }">
      <v-text-field
        v-bind="{ ...menuProps, ...$attrs }"
        :model-value="displayValue"
        :label="label"
        :variant="variant"
        :density="density"
        :disabled="disabled"
        :clearable="clearable"
        readonly
        append-inner-icon="mdi-calendar-clock"
        @click:clear.stop="clearValue"
        @keydown.enter.prevent="openMenu"
        @keydown.space.prevent="openMenu"
      />
    </template>

    <v-card class="ncrm-date-time-field__panel">
      <div class="ncrm-date-time-field__body">
        <v-date-picker
          :model-value="draftDateModel"
          color="primary"
          hide-header
          show-adjacent-months
          :first-day-of-week="1"
          @update:model-value="updateDraftDate"
        />

        <div class="ncrm-date-time-field__time" aria-label="HH:mm">
          <div class="ncrm-date-time-field__time-column">
            <div class="text-caption text-medium-emphasis px-2 pb-1">HH</div>
            <v-list density="compact" class="ncrm-date-time-field__time-list" aria-label="HH">
              <v-list-item
                v-for="hour in hours"
                :key="`hour-${hour}`"
                :active="draftParts.hour === hour"
                color="primary"
                rounded="lg"
                @click="updateDraftTime('hour', hour)"
              >
                <v-list-item-title class="text-center">
                  {{ pad(hour) }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </div>

          <div class="ncrm-date-time-field__time-column">
            <div class="text-caption text-medium-emphasis px-2 pb-1">MM</div>
            <v-list density="compact" class="ncrm-date-time-field__time-list" aria-label="MM">
              <v-list-item
                v-for="minute in minutes"
                :key="`minute-${minute}`"
                :active="draftParts.minute === minute"
                color="primary"
                rounded="lg"
                @click="updateDraftTime('minute', minute)"
              >
                <v-list-item-title class="text-center">
                  {{ pad(minute) }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </div>

      <v-divider />

      <v-card-actions class="ncrm-date-time-field__actions">
        <v-btn v-if="clearable" variant="text" color="primary" @click="clearValue">
          {{ $t("app.actions.clear") }}
        </v-btn>
        <v-spacer />
        <v-btn color="primary" variant="flat" @click="commitDraft">
          {{ $t("app.actions.ok") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import {
  DATE_TIME_FIELD_HOURS,
  DATE_TIME_FIELD_MINUTES,
  buildDateFromDateTimeParts,
  defaultDateTimeFieldParts,
  formatDateTimeFieldDisplay,
  formatDateTimeFieldValue,
  padDateTimePart,
  parseDateTimeFieldParts,
} from "@/Utile/dateTimeField.mjs";

export default {
  name: "NcrmDateTimeField",
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Date],
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "outlined",
    },
    density: {
      type: String,
      default: "comfortable",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    zIndex: {
      type: [Number, String],
      default: 2720,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      menuOpen: false,
      draftParts: parseDateTimeFieldParts(this.modelValue) || defaultDateTimeFieldParts(),
    };
  },
  computed: {
    displayValue() {
      return formatDateTimeFieldDisplay(parseDateTimeFieldParts(this.modelValue));
    },
    draftDateModel() {
      return buildDateFromDateTimeParts(this.draftParts || defaultDateTimeFieldParts());
    },
    hours() {
      return DATE_TIME_FIELD_HOURS;
    },
    minutes() {
      return DATE_TIME_FIELD_MINUTES;
    },
  },
  watch: {
    menuOpen(nextValue) {
      if (nextValue) {
        this.resetDraftFromValue();
      }
    },
  },
  methods: {
    pad(value) {
      return padDateTimePart(value);
    },
    openMenu() {
      if (this.disabled) return;
      this.menuOpen = true;
    },
    resetDraftFromValue() {
      this.draftParts = parseDateTimeFieldParts(this.modelValue) || defaultDateTimeFieldParts();
    },
    updateDraftDate(value) {
      const source = Array.isArray(value) ? value[0] : value;
      const parts = parseDateTimeFieldParts(source);
      if (!parts) return;
      this.draftParts = {
        ...this.draftParts,
        year: parts.year,
        month: parts.month,
        day: parts.day,
      };
    },
    updateDraftTime(field, value) {
      this.draftParts = {
        ...this.draftParts,
        [field]: Number(value),
      };
    },
    commitDraft() {
      this.$emit("update:modelValue", formatDateTimeFieldValue(this.draftParts));
      this.menuOpen = false;
    },
    clearValue() {
      if (this.disabled) return;
      this.$emit("update:modelValue", "");
      this.menuOpen = false;
    },
  },
};
</script>

<style scoped>
.ncrm-date-time-field__panel {
  border-radius: var(--app-radius-base);
  overflow: hidden;
}
.ncrm-date-time-field__body {
  display: flex;
  align-items: stretch;
}
.ncrm-date-time-field__time {
  display: grid;
  grid-template-columns: repeat(2, 68px);
  gap: 8px;
  padding: 12px;
  border-left: 1px solid var(--ncrm-color-border-default);
}
.ncrm-date-time-field__time-list {
  max-height: 288px;
  overflow-y: auto;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
}
.ncrm-date-time-field__actions {
  padding: 8px 12px;
}
@media (max-width: 640px) {
  .ncrm-date-time-field__body {
    flex-direction: column;
  }
  .ncrm-date-time-field__time {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-left: 0;
    border-top: 1px solid var(--ncrm-color-border-default);
  }
}
</style>
